<template>
  <div class="order-builder">
    <section class="page-body fade-in" v-if="!initialLoading">
      <div class="card-block">
        <div class="block-header">
          <div>
            <h2>Articles de la commande</h2>
            <p>Ajoutez un article à la fois avant de valider la commande.</p>
          </div>
          <div class="block-actions">
            <button
              class="btn btn-outline"
              @click="refreshAll"
              :disabled="refreshing || itemSubmitting"
            >
              <i class="bi bi-arrow-clockwise"></i>
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        <div class="product-selection">
          <div v-if="itemsLoading" class="product-state">
            <div class="loader"></div>
            <p>Chargement de la liste des produits…</p>
          </div>
          <div v-else-if="!productOptions.length" class="product-state">
            <i class="bi bi-box-seam"></i>
            <p>Aucun produit disponible pour le moment.</p>
          </div>
          <div v-else class="product-grid">
            <button
              v-for="product in productOptions"
              :key="product.reference || product.id || product.uuid"
              type="button"
              class="product-card"
              @click="openProductModal(product)"
              :disabled="itemSubmitting"
            >
              <div class="product-image-wrapper">
                <img
                  v-if="getImagePath(product)"
                  :src="$imageUrl(getImagePath(product))"
                  :alt="product.fullName"
                  @error="handleImageError($event)"
                />
                <div v-else class="product-placeholder">
                  <i class="bi bi-image"></i>
                </div>
              </div>
              <div class="product-card-body">
                <h4>{{ product.fullName }}</h4>
                <span class="product-price">
                  {{ formatAmount(getProductPrice(product)) }}
                </span>
              </div>
            </button>
          </div>
        </div>

        <div class="items-table-wrapper">
          <div v-if="itemsLoading" class="table-state">
            <div class="loader"></div>
            <p>Chargement des articles…</p>
          </div>
          <div v-else-if="!preparedOrderItems.length" class="table-state">
            <i class="bi bi-archive"></i>
            <p>Aucun article ajouté pour le moment.</p>
          </div>
          <table v-else class="items-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th class="numeric">Prix TTC</th>
                <th class="numeric">Quantité</th>
                <th class="numeric">Total TTC</th>
                <th>NB</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in preparedOrderItems" :key="line.id">
                <td>
                  <div class="cell-main">
                    <strong>{{ line.designation }}</strong>
                    <small v-if="line.reference"
                      >Réf. {{ line.reference }}</small
                    >
                  </div>
                </td>
                <td class="numeric">
                  <span class="badge badge-light">{{
                    formatAmount(line.price)
                  }}</span>
                </td>
                <td class="numeric">
                  <span class="badge badge-dark">{{
                    formatQuantity(line.quantity)
                  }}</span>
                </td>
                <td class="numeric">
                  <span class="badge badge-primary">{{
                    formatAmount(line.total)
                  }}</span>
                </td>
                <td>{{ line.remark || "—" }}</td>
                <td class="actions-col">
                  <button
                    class="btn btn-danger"
                    @click="deleteItem(line.raw)"
                    :disabled="itemSubmitting"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3">Total</th>
                <th class="numeric">{{ formatAmount(totalAmount) }}</th>
                <th colspan="2"></th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div
        v-if="showProductModal && activeProduct"
        class="product-modal-overlay"
        @click="closeProductModal"
      >
        <div class="product-modal" @click.stop>
          <header class="product-modal-header">
            <div>
              <h3>{{ activeProduct.fullName }}</h3>
              <p>
                Prix TTC : {{ formatAmount(getProductPrice(activeProduct)) }}
                <span
                  v-if="getProductStock(activeProduct) !== null"
                  class="stock-info"
                >
                  • Stock disponible :
                  {{ formatQuantity(getProductStock(activeProduct)) }} KG
                </span>
              </p>
            </div>
            <button class="btn btn-light icon-only" @click="closeProductModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>

          <section class="product-modal-body">
            <div v-if="getImagePath(activeProduct)" class="product-modal-image">
              <img
                :src="$imageUrl(getImagePath(activeProduct))"
                :alt="activeProduct.fullName"
                @error="handleImageError($event)"
              />
            </div>

            <div class="quantity-panels">
              <div class="quantity-display">
                <div class="quantity-display-header">
                  <span>Quantité (KG)</span>
                  <button
                    v-if="modalQuantity"
                    type="button"
                    class="btn-reset"
                    @click="resetQuantity"
                    title="Réinitialiser la quantité"
                  >
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                </div>
                <strong>{{ modalQuantity || "0" }}</strong>
              </div>
              <div class="quantity-display secondary">
                <span>Total TTC</span>
                <strong>{{ formatAmount(modalTotalAmount) }}</strong>
              </div>
            </div>

            <div class="quantity-keyboard">
              <button
                v-for="key in keypadLayout"
                :key="key.label"
                type="button"
                class="btn btn-keyboard"
                :class="{
                  danger: key.type === 'clear',
                  action: key.type === 'submit',
                }"
                @click="handleKeypadPress(key)"
                :disabled="itemSubmitting && key.type === 'submit'"
              >
                {{ key.label }}
              </button>
            </div>

            <div class="remark-group">
              <label for="modal-remark">Remarque (optionnel)</label>
              <textarea
                id="modal-remark"
                v-model.trim="modalRemark"
                rows="2"
                placeholder="Ajouter une note pour cet article…"
              ></textarea>
            </div>
          </section>

          <footer class="product-modal-footer">
            <button
              class="btn btn-light"
              type="button"
              @click="closeProductModal"
            >
              Annuler
            </button>
            <button
              class="btn btn-success"
              type="button"
              :disabled="!canConfirmModal || itemSubmitting"
              @click="confirmProductSelection"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!itemSubmitting">Ajouter</span>
              <span v-else>Ajout…</span>
            </button>
          </footer>
        </div>
      </div>

      <div
        v-if="showCustomerModal"
        class="customer-modal-overlay"
        @click="closeCustomerModal"
      >
        <div class="customer-modal" @click.stop>
          <header class="customer-modal-header">
            <div>
              <h3>Sélectionner un client</h3>
              <p>Choisissez le client pour cette commande</p>
            </div>
            <button class="btn btn-light icon-only" @click="closeCustomerModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>

          <section class="customer-modal-body">
            <div class="customer-search">
              <i class="bi bi-search"></i>
              <input
                v-model="customerSearchTerm"
                type="search"
                placeholder="Rechercher un client (nom, téléphone, ville...)"
              />
            </div>

            <div v-if="filteredCustomers.length === 0" class="customer-state">
              <i class="bi bi-people"></i>
              <p>Aucun client trouvé</p>
            </div>

            <div v-else class="customer-list">
              <button
                v-for="customer in filteredCustomers"
                :key="customer.reference || customer.id"
                type="button"
                class="customer-item"
                @click="selectCustomer(customer)"
              >
                <div class="customer-item-content">
                  <div class="customer-avatar">
                    {{ getCustomerInitials(customer) }}
                  </div>
                  <div class="customer-details">
                    <h4>
                      {{
                        customer.fullName ||
                        customer.name ||
                        customer.society ||
                        customer.reference
                      }}
                    </h4>
                    <div class="customer-meta">
                      <span v-if="customer.society" class="meta-item">
                        <i class="bi bi-building"></i>
                        {{ customer.society }}
                      </span>
                      <span v-if="customer.phone" class="meta-item">
                        <i class="bi bi-telephone"></i>
                        {{ customer.phone }}
                      </span>
                      <span
                        v-if="customer.city || customer.address"
                        class="meta-item"
                      >
                        <i class="bi bi-geo-alt"></i>
                        {{ customer.city || customer.address }}
                      </span>
                    </div>
                  </div>
                </div>
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </section>
        </div>
      </div>

      <div class="card-block">
        <h2>Détails de la commande</h2>
        <p class="block-subtitle">
          Renseignez le client et les informations principales. Les montants
          sont calculés automatiquement.
        </p>

        <form class="order-form" @submit.prevent="saveOrder">
          <div class="form-group">
            <label>Client *</label>
            <button
              type="button"
              class="form-control customer-select-btn"
              :class="{ 'has-error': formErrors.seller_customer_reference }"
              @click="openCustomerModal"
            >
              <span v-if="selectedCustomer">
                <i class="bi bi-person"></i>
                {{
                  selectedCustomer.fullName ||
                  selectedCustomer.name ||
                  selectedCustomer.society ||
                  selectedCustomer.reference
                }}
              </span>
              <span v-else class="placeholder">
                <i class="bi bi-person-plus"></i>
                Choisir un client
              </span>
              <i class="bi bi-chevron-down"></i>
            </button>
            <p v-if="formErrors.seller_customer_reference" class="error">
              {{ formErrors.seller_customer_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Statut</label>
            <select v-model="form.status" class="form-control">
              <option
                v-for="status in statuses"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Date d'échéance</label>
            <input v-model="form.due_date" type="date" class="form-control" />
          </div>

          <div class="form-group full">
            <label>Notes</label>
            <textarea
              v-model.trim="form.notes"
              rows="3"
              class="form-control"
              placeholder="Instructions complémentaires, remarques…"
            ></textarea>
          </div>

          <div class="summary-card">
            <div>
              <span>Total TTC</span>
              <strong>{{ formatAmount(totalAmount) }}</strong>
            </div>
            <div>
              <span>Nombre d'articles</span>
              <strong>{{ preparedOrderItems.length }}</strong>
            </div>
          </div>

          <p v-if="formErrors.items" class="error form-error-inline">
            {{ formErrors.items }}
          </p>

          <div class="form-actions">
            <button
              type="button"
              class="btn btn-light"
              @click="goBack"
              :disabled="submitting"
            >
              Annuler
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting || !canSubmit"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!submitting">Enregistrer</span>
              <span v-else>Enregistrement…</span>
            </button>
          </div>
        </form>
      </div>

      <p class="hint">
        <span class="text-danger">*</span> Champ obligatoire &nbsp;•&nbsp; Le
        montant total est calculé à partir des articles ajoutés.
      </p>
    </section>

    <section class="loading-state" v-else>
      <div class="loader"></div>
      <p>Préparation du formulaire…</p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OrderCreate",
  data() {
    const today = new Date().toISOString().split("T")[0];
    return {
      initialLoading: true,
      refreshing: false,
      itemsLoading: false,
      submitting: false,
      itemSubmitting: false,
      adjustingQuantity: false,
      adjustingPcs: false,
      orderItem: {
        item_reference: "",
        priceTTC: "",
        quantity: "",
        pcs: "",
        remark: "",
      },
      showProductModal: false,
      activeProduct: null,
      modalQuantity: "",
      modalRemark: "",
      showCustomerModal: false,
      customerSearchTerm: "",
      imageFallback:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23e2e8f0'/%3E%3Cg fill='%2394a3b8' font-family='sans-serif' font-size='12'%3E%3Ctext x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'%3ENo Image%3C/text%3E%3C/g%3E%3C/svg%3E",
      form: {
        reference: "",
        seller_customer_reference: "",
        status: "pending",
        due_date: today,
        notes: "",
        total_amount: 0,
      },
      itemErrors: {},
      formErrors: {},
      statuses: [
        { value: "pending", label: "En attente" },
        { value: "processing", label: "En cours" },
        { value: "completed", label: "Terminée" },
        { value: "cancelled", label: "Annulée" },
      ],
    };
  },
  computed: {
    ...mapGetters("sellerSaleItem", {
      saleItems: "getAll",
    }),
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    ...mapGetters("sellerOrderItem", {
      orderItems: "getAll",
    }),
    productOptions() {
      let source = [];
      if (Array.isArray(this.saleItems)) {
        source = this.saleItems;
      } else if (
        this.saleItems &&
        typeof this.saleItems === "object" &&
        Array.isArray(this.saleItems.data)
      ) {
        source = this.saleItems.data;
      }

      return source.map((entry) => {
        const itemData =
          (entry && typeof entry === "object" && entry.item) || {};
        const reference =
          entry?.item_reference != null
            ? entry?.item_reference
            : itemData?.reference != null
            ? itemData?.reference
            : itemData?.item_reference != null
            ? itemData?.item_reference
            : entry?.reference != null
            ? entry?.reference
            : "";
        const fullName =
          entry?.fullName != null
            ? entry?.fullName
            : itemData?.fullName != null
            ? itemData?.fullName
            : itemData?.name != null
            ? itemData?.name
            : itemData?.designation != null
            ? itemData?.designation
            : reference;
        const price =
          entry?.priceTTC != null
            ? entry?.priceTTC
            : entry?.price_ttc != null
            ? entry?.price_ttc
            : itemData?.priceTTC != null
            ? itemData?.priceTTC
            : itemData?.price_ttc != null
            ? itemData?.price_ttc
            : itemData?.price != null
            ? itemData?.price
            : itemData?.unit_price != null
            ? itemData?.unit_price
            : null;
        const pcsCandidate =
          entry?.pcs != null
            ? entry?.pcs
            : itemData?.pcs != null
            ? itemData?.pcs
            : itemData?.items_per_pack != null
            ? itemData?.items_per_pack
            : itemData?.packaging != null
            ? itemData?.packaging
            : null;
        const normalizedPcs =
          pcsCandidate === null || pcsCandidate === undefined
            ? null
            : Number.isFinite(Number(pcsCandidate))
            ? Number(pcsCandidate)
            : pcsCandidate;
        const image =
          entry?.image != null
            ? entry?.image
            : entry?.item_image != null
            ? entry?.item_image
            : itemData?.image != null
            ? itemData?.image
            : itemData?.image_url != null
            ? itemData?.image_url
            : itemData?.photo != null
            ? itemData?.photo
            : itemData?.picture != null
            ? itemData?.picture
            : null;

        return {
          ...entry,
          reference,
          fullName,
          priceTTC: price,
          pcs: normalizedPcs,
          image,
          item: itemData,
          rawSaleItem: entry,
        };
      });
    },
    selectedProduct() {
      return (
        this.productOptions.find(
          (product) => product.reference === this.orderItem.item_reference
        ) || null
      );
    },
    preparedOrderItems() {
      return (this.orderItems || []).map((item) => {
        const quantity = Number(
          item.quantity != null
            ? item.quantity
            : item.qty != null
            ? item.qty
            : 0
        );
        const price = Number(
          item.priceTTC != null
            ? item.priceTTC
            : item.price_ttc != null
            ? item.price_ttc
            : item.unit_price != null
            ? item.unit_price
            : 0
        );
        const total = Number(
          item.totalPriceTTC != null
            ? item.totalPriceTTC
            : item.total_price_ttc != null
            ? item.total_price_ttc
            : price * quantity
        );
        return {
          id:
            item.id ||
            item.uuid ||
            `${item.item_reference}-${item.id || Date.now()}`,
          reference: item.item_reference || item.reference,
          designation:
            item.itemFullName ||
            item.item_full_name ||
            item.item?.fullName ||
            item.item?.name ||
            item.designation ||
            item.item_reference ||
            "Article",
          price,
          quantity,
          total,
          remark: item.remark || item.note || "",
          raw: item,
        };
      });
    },
    totalAmount() {
      return this.preparedOrderItems.reduce(
        (sum, item) => sum + Number(item.total || 0),
        0
      );
    },
    canSubmit() {
      return (
        !!this.form.seller_customer_reference &&
        this.preparedOrderItems.length > 0
      );
    },
    modalTotalAmount() {
      if (!this.activeProduct) return 0;
      const quantity = Number(this.modalQuantity);
      const price = Number(this.getProductPrice(this.activeProduct) || 0);
      const total = quantity * price;
      return Number.isFinite(total) ? total : 0;
    },
    canConfirmModal() {
      const quantity = Number(this.modalQuantity);
      return (
        !!this.activeProduct &&
        !Number.isNaN(quantity) &&
        quantity > 0 &&
        Number.isFinite(quantity)
      );
    },
    keypadLayout() {
      return [
        { label: "7", value: "7" },
        { label: "8", value: "8" },
        { label: "9", value: "9" },
        { label: "⌫", type: "backspace" },
        { label: "4", value: "4" },
        { label: "5", value: "5" },
        { label: "6", value: "6" },
        { label: "C", type: "clear" },
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "0", value: "0" },
        { label: "00", value: "00" },
        { label: ".", value: "." },
      ];
    },
    selectedCustomer() {
      if (!this.form.seller_customer_reference) return null;
      return (
        (this.customers || []).find(
          (c) => c.reference === this.form.seller_customer_reference
        ) || null
      );
    },
    filteredCustomers() {
      if (!this.customerSearchTerm) return this.customers || [];
      const term = this.customerSearchTerm.toLowerCase();
      return (this.customers || []).filter((customer) =>
        [
          customer.fullName,
          customer.name,
          customer.society,
          customer.phone,
          customer.email,
          customer.city,
          customer.address,
          customer.reference,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase())
          .some((value) => value.includes(term))
      );
    },
  },
  watch: {
    selectedProduct: {
      handler(product) {
        if (!product) {
          this.orderItem.priceTTC = "";
          return;
        }
        const price =
          product.priceTTC != null
            ? product.priceTTC
            : product.price_ttc != null
            ? product.price_ttc
            : product.item?.priceTTC != null
            ? product.item?.priceTTC
            : product.item?.price_ttc != null
            ? product.item?.price_ttc
            : product.price != null
            ? product.price
            : product.unit_price;
        const numericPrice = Number(price);
        if (!Number.isNaN(numericPrice)) {
          this.orderItem.priceTTC = numericPrice.toFixed(2);
        } else if (price !== undefined && price !== null) {
          this.orderItem.priceTTC = price;
        }
        if (this.orderItem.pcs) {
          this.updateQuantityFromPcs(this.orderItem.pcs);
        } else if (
          this.orderItem.quantity &&
          product.pcs !== undefined &&
          product.pcs !== null
        ) {
          this.updatePcsFromQuantity(this.orderItem.quantity);
        }
      },
    },
    totalAmount: {
      immediate: true,
      handler(value) {
        this.form.total_amount = Number(
          value.toFixed ? value.toFixed(2) : value
        );
      },
    },
    "orderItem.pcs"(newValue) {
      if (this.adjustingPcs) return;
      if (!newValue) return;
      this.updateQuantityFromPcs(newValue);
    },
    "orderItem.quantity"(newValue) {
      if (this.adjustingQuantity) return;
      if (!newValue) return;
      this.updatePcsFromQuantity(newValue);
    },
  },
  methods: {
    openProductModal(product) {
      if (!product) return;
      this.itemErrors = {};
      this.activeProduct = product;
      this.modalQuantity = "";
      this.modalRemark = "";
      this.showProductModal = true;
    },
    closeProductModal() {
      if (this.itemSubmitting) return;
      this.showProductModal = false;
      this.activeProduct = null;
      this.modalQuantity = "";
      this.modalRemark = "";
      this.itemErrors = {};
    },
    goBack() {
      this.$router.back();
    },
    openCustomerModal() {
      this.customerSearchTerm = "";
      this.showCustomerModal = true;
    },
    closeCustomerModal() {
      this.showCustomerModal = false;
      this.customerSearchTerm = "";
    },
    selectCustomer(customer) {
      if (!customer || !customer.reference) return;
      this.form.seller_customer_reference = customer.reference;
      this.closeCustomerModal();
    },
    getCustomerInitials(customer) {
      const name = customer.fullName || customer.name || customer.society || "";
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      const initials = [parts[0], parts[parts.length - 1]]
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .join("");
      return initials || name.charAt(0).toUpperCase();
    },
    getImagePath(product) {
      if (!product) {
        return "";
      }
      return product.image != null
        ? product.image
        : product.item?.image != null
        ? product.item?.image
        : product.item?.image_url != null
        ? product.item?.image_url
        : product.item?.photo != null
        ? product.item?.photo
        : "";
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.src = this.imageFallback;
      }
    },
    getProductPrice(product) {
      if (!product) return 0;
      const price =
        product.priceTTC != null
          ? product.priceTTC
          : product.price_ttc != null
          ? product.price_ttc
          : product.item?.priceTTC != null
          ? product.item?.priceTTC
          : product.item?.price_ttc != null
          ? product.item?.price_ttc
          : product.price != null
          ? product.price
          : product.unit_price != null
          ? product.unit_price
          : 0;
      return Number(price) || 0;
    },
    getProductStock(product) {
      if (!product) return null;
      const stock =
        product.quantity != null
          ? product.quantity
          : product.qty != null
          ? product.qty
          : product.stock != null
          ? product.stock
          : product.item?.quantity != null
          ? product.item?.quantity
          : product.item?.qty != null
          ? product.item?.qty
          : product.item?.stock != null
          ? product.item?.stock
          : product.rawSaleItem?.quantity != null
          ? product.rawSaleItem?.quantity
          : product.rawSaleItem?.qty != null
          ? product.rawSaleItem?.qty
          : product.rawSaleItem?.stock != null
          ? product.rawSaleItem?.stock
          : null;
      return stock !== null ? Number(stock) : null;
    },
    resetQuantity() {
      this.modalQuantity = "";
    },
    formatAmount(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatQuantity(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      const hasDecimals = number % 1 !== 0;
      return hasDecimals ? number.toFixed(2) : number.toString();
    },
    handleKeypadPress(key) {
      if (!key) return;
      if (key.type === "clear") {
        this.modalQuantity = "";
        return;
      }
      if (key.type === "backspace") {
        this.modalQuantity = this.modalQuantity.slice(
          0,
          Math.max(0, this.modalQuantity.length - 1)
        );
        return;
      }
      const value = key.value;
      if (value === undefined) {
        return;
      }
      if (value === ".") {
        if (this.modalQuantity.includes(".")) {
          return;
        }
        this.modalQuantity = this.modalQuantity
          ? `${this.modalQuantity}.`
          : "0.";
        return;
      }
      if (value === "00") {
        if (!this.modalQuantity) return;
        this.modalQuantity += "00";
        return;
      }
      if (this.modalQuantity === "0") {
        this.modalQuantity = value;
      } else {
        this.modalQuantity += value;
      }
    },
    async confirmProductSelection() {
      if (!this.activeProduct) return;
      const quantity = Number(this.modalQuantity);
      if (!this.canConfirmModal || Number.isNaN(quantity)) {
        this.itemErrors = {
          ...this.itemErrors,
          quantity: "Indiquez une quantité valide.",
        };
        return;
      }
      this.orderItem.item_reference = this.activeProduct.reference || "";
      const price = this.getProductPrice(this.activeProduct);
      const numericPrice = Number(price);
      this.orderItem.priceTTC = Number.isNaN(numericPrice)
        ? price
        : numericPrice.toFixed(2);
      this.orderItem.quantity = quantity;
      this.orderItem.remark = this.modalRemark || "";
      if (!this.orderItem.item_reference) {
        this.$toast?.error?.("Référence produit introuvable.");
        return;
      }
      if (
        this.activeProduct.pcs !== undefined &&
        this.activeProduct.pcs !== null
      ) {
        this.updatePcsFromQuantity(quantity);
      } else {
        this.orderItem.pcs = "";
      }
      const saved = await this.saveItem();
      if (saved) {
        this.closeProductModal();
      }
    },
    async loadInitialData() {
      this.initialLoading = true;
      try {
        this.itemsLoading = true;
        await Promise.all([
          this.$store.dispatch("sellerSaleItem/getAll"),
          this.$store.dispatch("sellerOrderItem/getAll"),
          this.customers && this.customers.length
            ? Promise.resolve()
            : this.$store.dispatch("customer/getAll"),
        ]);
      } catch (error) {
        console.error("Error preparing order creation", error);
        this.$toast?.error?.(
          "Impossible de préparer le formulaire de commande."
        );
      } finally {
        this.itemsLoading = false;
        this.initialLoading = false;
      }
    },
    async refreshAll() {
      this.refreshing = true;
      try {
        this.itemsLoading = true;
        await Promise.all([
          this.$store.dispatch("sellerSaleItem/getAll"),
          this.$store.dispatch("sellerOrderItem/getAll"),
        ]);
        this.$toast?.success?.("Liste des articles mise à jour.");
      } catch (error) {
        console.error("Error refreshing lists", error);
        this.$toast?.error?.("Impossible d'actualiser les listes.");
      } finally {
        this.itemsLoading = false;
        this.refreshing = false;
      }
    },
    validateItem() {
      const errors = {};
      if (!this.orderItem.item_reference) {
        errors.item_reference = "Sélectionnez un produit.";
      }
      const quantity = Number(this.orderItem.quantity);
      if (!this.orderItem.quantity || Number.isNaN(quantity) || quantity <= 0) {
        errors.quantity = "Indiquez une quantité valide.";
      }
      const price = Number(this.orderItem.priceTTC || 0);
      if (Number.isNaN(price) || price < 0) {
        errors.priceTTC = "Le prix doit être positif.";
      }
      this.itemErrors = errors;
      return Object.keys(errors).length === 0;
    },
    resetItemForm() {
      this.orderItem = {
        item_reference: "",
        priceTTC: "",
        quantity: "",
        pcs: "",
        remark: "",
      };
      this.itemErrors = {};
    },
    buildItemPayload() {
      const quantity = Number(this.orderItem.quantity);
      const price = Number(this.orderItem.priceTTC || 0);
      return {
        item_reference: this.orderItem.item_reference,
        quantity,
        pcs: this.orderItem.pcs ? Number(this.orderItem.pcs) : null,
        priceTTC: price,
        remark: this.orderItem.remark || "",
        totalPriceTTC: Number((quantity * price).toFixed(2)),
      };
    },
    async saveItem() {
      if (this.itemSubmitting) return false;
      if (!this.validateItem()) return false;
      this.itemSubmitting = true;
      let success = false;
      try {
        const payload = this.buildItemPayload();
        await this.$store.dispatch("sellerOrderItem/store", payload);
        await this.$store.dispatch("sellerOrderItem/getAll");
        this.resetItemForm();
        this.$toast?.success?.("Article ajouté à la commande.");
        success = true;
      } catch (error) {
        console.error("Error saving order item", error);
        this.$toast?.error?.("Impossible d'ajouter l'article.");
      } finally {
        this.itemSubmitting = false;
      }
      return success;
    },
    async deleteItem(item) {
      if (!item) return;
      const reference = item.id || item.reference || item.item_reference;
      if (!reference) return;
      const confirmed = await this.$confirm?.({
        title: "Supprimer l'article",
        message: "Voulez-vous retirer cet article de la commande ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (confirmed === false) {
        return;
      }
      this.itemSubmitting = true;
      try {
        await this.$store.dispatch("sellerOrderItem/destroy", reference);
        await this.$store.dispatch("sellerOrderItem/getAll");
        this.$toast?.success?.("Article supprimé.");
      } catch (error) {
        console.error("Error deleting order item", error);
        this.$toast?.error?.("Impossible de supprimer l'article.");
      } finally {
        this.itemSubmitting = false;
      }
    },
    validateForm() {
      const errors = {};

      if (!this.form.seller_customer_reference) {
        errors.seller_customer_reference = "Veuillez sélectionner un client.";
      }
      if (this.preparedOrderItems.length === 0) {
        errors.items = "Ajoutez au moins un article avant d'enregistrer.";
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    buildOrderPayload() {
      const payload = {
        seller_customer_reference: this.form.seller_customer_reference,
        status: this.form.status,
        total_amount: this.totalAmount,
        due_date: this.form.due_date,
        notes: this.form.notes,
      };
      return payload;
    },
    async saveOrder() {
      if (!this.validateForm() || this.submitting) return;
      this.submitting = true;
      try {
        const payload = this.buildOrderPayload();
        await this.$store.dispatch("sellerOrder/store", payload);
        this.$toast?.success?.("Commande créée avec succès.");
        this.$router.push({ name: "orders-list" });
      } catch (error) {
        console.error("Error creating order", error);
        this.$toast?.error?.("Impossible de créer la commande.");
      } finally {
        this.submitting = false;
      }
    },
    updateQuantityFromPcs(pcsValue) {
      if (!this.selectedProduct || !pcsValue) return;
      const pcsPerUnit = Number(
        this.selectedProduct.pcs != null
          ? this.selectedProduct.pcs
          : this.selectedProduct.items_per_pack != null
          ? this.selectedProduct.items_per_pack
          : this.selectedProduct.item?.pcs != null
          ? this.selectedProduct.item?.pcs
          : this.selectedProduct.item?.items_per_pack != null
          ? this.selectedProduct.item?.items_per_pack
          : 0
      );
      if (!pcsPerUnit) return;
      this.adjustingQuantity = true;
      const quantity = Number(pcsValue) / pcsPerUnit;
      this.orderItem.quantity = Number.isFinite(quantity)
        ? Number(quantity.toFixed(2))
        : this.orderItem.quantity;
      this.adjustingQuantity = false;
    },
    updatePcsFromQuantity(quantityValue) {
      if (!this.selectedProduct || !quantityValue) return;
      const pcsPerUnit = Number(
        this.selectedProduct.pcs != null
          ? this.selectedProduct.pcs
          : this.selectedProduct.items_per_pack != null
          ? this.selectedProduct.items_per_pack
          : this.selectedProduct.item?.pcs != null
          ? this.selectedProduct.item?.pcs
          : this.selectedProduct.item?.items_per_pack != null
          ? this.selectedProduct.item?.items_per_pack
          : 0
      );
      if (!pcsPerUnit) return;
      this.adjustingPcs = true;
      const pcs = Number(quantityValue) * pcsPerUnit;
      this.orderItem.pcs = Number.isFinite(pcs)
        ? Number(pcs.toFixed(2))
        : this.orderItem.pcs;
      this.adjustingPcs = false;
    },
  },
  mounted() {
    this.loadInitialData();
  },
};
</script>

<style scoped>
.order-builder {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.card-block {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.block-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.block-header h2 {
  margin: 0;
  font-size: 1.45rem;
  color: #0f172a;
}

.block-header p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.block-actions {
  display: flex;
  gap: 0.75rem;
}

.block-subtitle {
  margin: -0.5rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.product-selection {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.product-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  border-radius: 18px;
  color: #6b7280;
  text-align: center;
}

.product-state i {
  font-size: 2rem;
  color: #2563eb;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
}

.product-card {
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 18px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.product-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.product-card:not(:disabled):hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
  border-color: rgba(79, 70, 229, 0.6);
}

.product-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #94a3b8;
  font-size: 2rem;
}

.product-card-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-align: center;
}

.product-card-body h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
}

.product-price {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2563eb;
}

.product-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
}

.product-modal {
  width: min(520px, 100%);
  max-height: 70vh;
  background: #ffffff;
  border-radius: 26px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
}

.product-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
}

.product-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.product-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.stock-info {
  color: #059669;
  font-weight: 600;
}

.btn.icon-only {
  padding: 0.5rem;
  border-radius: 12px;
}

.product-modal-body {
  padding: 0 1.75rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.product-modal-image {
  display: flex;
  justify-content: center;
}

.product-modal-image img {
  width: 140px;
  height: 140px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.38);
}

.quantity-panels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.quantity-display {
  background: #f8fafc;
  border-radius: 18px;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
}

.quantity-display-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.quantity-display span {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.btn-reset {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn-reset:hover {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.btn-reset i {
  font-size: 0.95rem;
}

.quantity-display strong {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  font-family: "Roboto Mono", monospace;
}

.quantity-display.secondary {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.quantity-keyboard {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.btn-keyboard {
  border: none;
  border-radius: 16px;
  padding: 0.85rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #4f46e5, #2563eb);
  box-shadow: 0 12px 22px rgba(79, 70, 229, 0.32);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-keyboard:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 25px rgba(79, 70, 229, 0.36);
}

.btn-keyboard:active {
  transform: translateY(0);
}

.btn-keyboard.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  box-shadow: 0 12px 22px rgba(239, 68, 68, 0.28);
}

.btn-keyboard.action {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 12px 22px rgba(16, 185, 129, 0.28);
}

.remark-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remark-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.remark-group textarea {
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #0f172a;
  padding: 0.85rem 1rem;
  resize: vertical;
  min-height: 70px;
}

.remark-group textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.product-modal-footer {
  padding: 1.5rem 1.75rem 1.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.product-modal-footer .btn {
  min-width: 130px;
}

.customer-select-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  text-align: left;
  cursor: pointer;
  background: white;
}

.customer-select-btn .placeholder {
  color: #9ca3af;
}

.customer-select-btn i:last-child {
  margin-left: auto;
  color: #6b7280;
  font-size: 0.9rem;
}

.customer-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
}

.customer-modal {
  width: min(600px, 100%);
  max-height: 85vh;
  background: #ffffff;
  border-radius: 26px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
}

.customer-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.customer-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.customer-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.customer-modal-body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.customer-search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.customer-search:focus-within {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.customer-search i {
  color: #94a3b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.customer-search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.95rem;
  width: 100%;
  color: #1e293b;
}

.customer-search input::placeholder {
  color: #94a3b8;
}

.customer-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: #94a3b8;
  text-align: center;
}

.customer-state i {
  font-size: 3rem;
  color: #cbd5e1;
}

.customer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.customer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
}

.customer-item:hover {
  background: #f8fafc;
  border-color: #3b82f6;
  transform: translateX(4px);
}

.customer-item:active {
  transform: translateX(2px);
}

.customer-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.customer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  flex-shrink: 0;
}

.customer-details {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.customer-details h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.customer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: #64748b;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-item i {
  font-size: 0.9rem;
  color: #94a3b8;
}

.customer-item i:last-child {
  color: #cbd5e1;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.form-group label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.items-table-wrapper {
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 20px;
  overflow: hidden;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table th,
.items-table td {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  text-align: left;
  font-size: 0.95rem;
  color: #1f2937;
}

.items-table th.numeric,
.items-table td.numeric {
  text-align: right;
}

.items-table tfoot th {
  font-size: 1rem;
  font-weight: 700;
  background: rgba(37, 99, 235, 0.06);
}

.actions-col {
  text-align: right;
  width: 70px;
}

.cell-main {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.cell-main strong {
  font-weight: 600;
  color: #111827;
}

.cell-main small {
  color: #9ca3af;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 0.25rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge-light {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.badge-dark {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.badge-primary {
  background: rgba(34, 197, 94, 0.16);
  color: #0f766e;
}

.order-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.order-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full {
  grid-column: 1 / -1;
}

.summary-card {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.1),
    rgba(79, 70, 229, 0.12)
  );
  color: #1e293b;
}

.summary-card span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.summary-card strong {
  font-size: 1.25rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-success {
  background: rgba(34, 197, 94, 0.16);
  color: #047857;
  border: none;
}

.btn-success:hover {
  background: rgba(34, 197, 94, 0.24);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: none;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.error {
  color: #ef4444;
  font-size: 0.83rem;
}

.form-error-inline {
  grid-column: 1 / -1;
  margin: -0.25rem 0 0;
}

.has-error {
  border-color: #ef4444;
}

.hint {
  color: #6b7280;
  font-size: 0.85rem;
  text-align: center;
}

.text-danger {
  color: #ef4444;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: #6b7280;
}

.table-state i {
  font-size: 2rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

@media (max-width: 992px) {
  .order-builder {
    padding: 1.5rem 1.5rem 4rem;
  }
  .order-form {
    grid-template-columns: 1fr;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .order-builder {
    padding: 1.25rem 1rem 4rem;
  }
  .card-block {
    padding: 1.5rem;
  }
  .item-form {
    grid-template-columns: 1fr;
  }
  .block-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .block-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .items-table-wrapper {
    overflow-x: auto;
  }
  .items-table {
    min-width: 680px;
  }
  .product-modal {
    border-radius: 20px 20px 0 0;
    max-height: 70vh;
  }
  .product-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .product-modal-body {
    padding: 0 1.5rem 1.5rem;
  }
  .product-modal-footer {
    padding: 1.25rem 1.5rem 1.5rem;
  }
  .customer-modal {
    border-radius: 20px 20px 0 0;
    max-height: 94vh;
  }
  .customer-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .customer-modal-body {
    padding: 0 1.5rem 1.5rem;
  }
  .customer-modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  .quantity-display strong {
    font-size: 1.6rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .card-block {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
  }
  .items-table {
    min-width: 580px;
  }
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .quantity-display {
    padding: 1rem 1.1rem;
  }
  .btn-keyboard {
    padding: 0.75rem;
    font-size: 1.1rem;
  }
}
</style>
